import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import Badge from "@mui/material/Badge";
import Button, { type ButtonProps } from "@mui/material/Button";
import Checkbox, { type CheckboxProps } from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled, useTheme, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import {
  MRT_BottomToolbar,
  MRT_TableBodyCellValue,
  MRT_ToggleRowActionMenuButton,
  MRT_TopToolbar,
  type MRT_Cell,
  type MRT_Column,
  type MRT_SortingState,
  type MRT_Row,
  type MRT_RowData,
  type MRT_TableInstance,
} from "material-react-table";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  type UIEventHandler,
} from "react";
import { columnGroupings, columnLabel, tableCardLayout } from "_shared/_views/tables/table-card-layout";
import { countSetFilters, filterableColumns, TableFilterSheet } from "_shared/_views/tables/table-filter-sheet";
import { TableSelectCheckbox } from "_shared/_views/tables/table-select-checkbox";
import { currentSortLabel, sortableColumns, TableSortSheet } from "_shared/_views/tables/table-sort-sheet";
import { TOUCH_TARGET } from "_shared/_views/touch-target";

/**
 * A list table on a phone, drawn as cards (#6715). A table asks for it with `MRTNoMemo`'s `cards`.
 *
 * Nothing here is written per page. A card is read off the table's own columns (`tableCardLayout`) and
 * each value is drawn by its column's own `Cell`, so a formatter a column gains reaches its cards too.
 * The table's toolbars are MRT's own: the heading, chips, search, the ⋯ tools menu and the bulk-action
 * banner above, and the pager below. Only the rows change.
 *
 * Nothing the table offers is lost on the way:
 *
 * - **Sort** and **Filter** open bottom sheets listing every column the table sorts or filters by,
 *   hidden or shown. The tools menu's Column filters opens the same Filter sheet. Both are icons in the
 *   search row (`TableCardSortAndFilter`), which `TableToolsMenu` draws before its ⋯, so the search,
 *   Sort, Filter and the ⋯ share one row (#6716, #7224). A table whose toolbar has no tools menu gets
 *   them as a row above the cards.
 * - **Show/hide** decides a card's fields, since a card reads only the columns shown.
 * - **The card is the row**: a tap does what a click on the row does, the name stays a link, and the
 *   row's ⋯ sits in the card's corner.
 * - **Each card shows its checkbox** where the table selects rows for a bulk action, as each row of
 *   the table does, and the checkbox is what selects it (#6720). A long press selected a card before,
 *   which nothing on screen told a reader, and a tap then selected rather than opened. Now a tap opens
 *   the card whatever is selected.
 *   A table whose rows are ticks (`cards={{ ticks: true }}`, such as a template picker) has nothing to
 *   open: a tap anywhere on the card ticks it (#7163).
 * - **Select all** leads the Sort and Filter line where the table's header would offer it, and does what
 *   that header's checkbox does: the same handler, over the same rows (#7171).
 *   While Sort and Filter are in the search row, Select all keeps the line above the cards on its own.
 * - **Sub-rows** open under their card from an expander in its corner, each a card of its own, set in
 *   by its depth.
 * - **Sort** may sit in the table's own toolbar instead (`cards={{ sortInToolbar: true }}`), where a
 *   table draws `TableSortButton` beside its other controls and spends no line on it.
 * - **The table's scroll** is heard on the card list: the `ref` and `onScroll` of
 *   `muiTableContainerProps` reach the list, so a table that asks for more rows as the reader nears the
 *   end of its container still does (#7163).
 */

/** How a table's cards behave, beyond being drawn. See `MRTNoMemo`'s `cards`. */
export type TableCardsOptions = {
  /** The rows are ticks rather than things to open: every card shows its checkbox, and a tap ticks it. */
  ticks?: boolean;
  /** The table draws `TableSortButton` in its own toolbar, so the card list draws no Sort of its own. */
  sortInToolbar?: boolean;
  /**
   * The list's own order, for a table that holds its sorting in `state` (#7224). Sort carries a dot while
   * the list is sorted any other way. Without it the default is `initialState.sorting`, or no sorting.
   *
   * The page names it because the table cannot tell it from `state`: Proposals restores the reader's
   * order from the address, so the order a reload starts in is the reader's, not the page's.
   */
  defaultSorting?: readonly ColumnSort[];
};

/** One column of a table's sorting. */
type ColumnSort = MRT_SortingState[number];

const noSorting: readonly ColumnSort[] = [];

function parseFromValuesOrFunc<TArgs, TValue>(
  valueOrFunc: TValue | ((args: TArgs) => TValue) | undefined,
  args: TArgs,
): TValue | undefined {
  return typeof valueOrFunc === "function" ? (valueOrFunc as (args: TArgs) => TValue)(args) : valueOrFunc;
}

const SortAndFilterRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1, 1),
  "& .MuiButton-root": { minHeight: TOUCH_TARGET, minWidth: 0 },
}));

/** Sort and Filter in the search row are only ever drawn on a phone, so they always take a fingertip's size. */
const toolbarIconSx = { height: TOUCH_TARGET, width: TOUCH_TARGET } as const;

const SelectAllLabel = styled("label")(({ theme }) => ({
  alignItems: "center",
  cursor: "pointer",
  display: "inline-flex",
  flex: "0 0 auto",
  gap: theme.spacing(0.5),
  minHeight: TOUCH_TARGET,
  // The checkbox sits flush with the cards' own, which are set in by the card's padding.
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  ...theme.typography.body2,
}));

const SortButtonLabel = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const CardList = styled("ul")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  gap: theme.spacing(1),
  listStyle: "none",
  margin: 0,
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(0, 1, 1),
}));

const CardItem = styled("li")({ minWidth: 0 });

/** How far a sub-row's card is set in under its parent's, per level, in theme spacing units. */
const SUB_ROW_INDENT = 2;

const Card = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  minWidth: 0,
  padding: theme.spacing(1.5, 0.5, 1.5, 2),
  WebkitTapHighlightColor: "transparent",
  "&[data-selected='true']": {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    borderColor: theme.palette.primary.main,
  },
}));

const CardHeading = styled("div")({
  alignItems: "flex-start",
  display: "flex",
  gap: 4,
  minWidth: 0,
});

/**
 * The title's first line sits on the center of the checkbox beside it: a small checkbox is 38px tall and
 * raised 6px, so its center is 13px down, and a 20px line starting 3px down is centered there. Centering the
 * whole title in the heading instead put it 6px below the box, whose row is as tall as the box (#6719).
 */
const CardTitle = styled("div")(({ theme }) => ({
  paddingTop: 3,
  flex: "1 1 auto",
  fontWeight: theme.typography.fontWeightMedium,
  minWidth: 0,
  overflow: "hidden",
}));

const CardCorner = styled("div")({ display: "flex", flexShrink: 0, marginTop: -6 });

const CardFields = styled("dl")(({ theme }) => ({
  columnGap: theme.spacing(1.5),
  display: "grid",
  gridTemplateColumns: "max-content minmax(0, 1fr)",
  margin: 0,
  paddingRight: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  "& dt": { color: theme.palette.text.secondary, ...theme.typography.body2 },
  "& dd": { margin: 0, minWidth: 0, overflow: "hidden", ...theme.typography.body2 },
}));

const CardStatus = styled("div")({ display: "flex", minWidth: 0 });

function cellOf<TData extends MRT_RowData>(row: MRT_Row<TData>, columnId: string): MRT_Cell<TData> | undefined {
  return row.getAllCells().find((cell) => cell.column.id === columnId);
}

function CellValue<TData extends MRT_RowData>(
  props: Readonly<{ row: MRT_Row<TData>; columnId: string; rowIndex: number; table: MRT_TableInstance<TData> }>,
) {
  const cell = cellOf(props.row, props.columnId);
  if (!cell) return null;
  return <MRT_TableBodyCellValue cell={cell} staticRowIndex={props.rowIndex} table={props.table} />;
}

/** Opens or closes the row's sub-rows, which follow its card as cards of their own. */
function CardExpander<TData extends MRT_RowData>(
  props: Readonly<{ row: MRT_Row<TData>; table: MRT_TableInstance<TData> }>,
) {
  const { row, table } = props;
  const isExpanded = row.getIsExpanded();
  const { localization } = table.options;
  return (
    <IconButton
      aria-expanded={isExpanded}
      aria-label={isExpanded ? localization.collapse : localization.expand}
      size="small"
      sx={{ height: TOUCH_TARGET, width: TOUCH_TARGET }}
      onClick={(event) => {
        event.stopPropagation();
        row.toggleExpanded();
      }}
    >
      <ExpandMoreIcon sx={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
    </IconButton>
  );
}

/** The row's own actions, as the table's actions column draws them. */
function rowActionsOf<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  row: MRT_Row<TData>,
  rowIndex: number,
): ReactNode {
  const { enableRowActions, renderRowActions, renderRowActionMenuItems } = table.options;
  const cell = cellOf(row, "mrt-row-actions");
  if (!enableRowActions || !cell) return null;
  if (renderRowActions) return renderRowActions({ cell, row, staticRowIndex: rowIndex, table });
  if (renderRowActionMenuItems) {
    return <MRT_ToggleRowActionMenuButton cell={cell} row={row} staticRowIndex={rowIndex} table={table} />;
  }
  return null;
}

function TableCard<TData extends MRT_RowData>(
  props: Readonly<{
    row: MRT_Row<TData>;
    rowIndex: number;
    table: MRT_TableInstance<TData>;
    ticks: boolean;
  }>,
) {
  "use no memo";

  const { row, rowIndex, table, ticks } = props;
  const layout = tableCardLayout(table.getVisibleLeafColumns() as MRT_Column<TData>[]);
  const rowProps = parseFromValuesOrFunc(table.options.muiTableBodyRowProps, {
    isDetailPanel: false,
    row,
    staticRowIndex: rowIndex,
    table,
  });
  // The table selects rows at all: every card shows its checkbox, ticked or not, enabled or not.
  const selectsRows = Boolean(table.options.enableRowSelection);
  const canSelect = selectsRows && row.getCanSelect();
  const isSelected = row.getIsSelected();
  // The table's own say over the checkbox, as its select column draws it: a partly used row's dash, the
  // reason a row cannot be ticked.
  const checkboxProps = parseFromValuesOrFunc(table.options.muiSelectCheckboxProps, {
    row,
    staticRowIndex: rowIndex,
    table,
  }) as CheckboxProps | undefined;
  function onClick(event: MouseEvent<HTMLDivElement>) {
    if (ticks) {
      if (canSelect) row.toggleSelected();
      return;
    }
    (rowProps?.onClick as ((event: MouseEvent<HTMLElement>) => void) | undefined)?.(event);
  }

  const actions = rowActionsOf(table, row, rowIndex);
  const canExpand = row.getCanExpand();

  return (
    <Card
      variant="outlined"
      data-testid="table-card"
      data-selected={isSelected}
      onClick={onClick}
      sx={{ cursor: (ticks ? canSelect : rowProps?.onClick) ? "pointer" : "default" }}
    >
      <CardHeading>
        {selectsRows && (
          <Checkbox
            checked={isSelected}
            disabled={!canSelect}
            size="small"
            sx={{ marginTop: -0.75, marginLeft: -1.5 }}
            {...checkboxProps}
            slotProps={{ input: { "aria-label": "Select" } }}
            onClick={(event) => event.stopPropagation()}
            onChange={() => row.toggleSelected()}
          />
        )}
        <CardTitle>
          {layout.title && <CellValue row={row} columnId={layout.title.id} rowIndex={rowIndex} table={table} />}
        </CardTitle>
        {(actions || canExpand) && (
          <CardCorner>
            {actions}
            {canExpand && <CardExpander row={row} table={table} />}
          </CardCorner>
        )}
      </CardHeading>
      {layout.status && (
        <CardStatus>
          <CellValue row={row} columnId={layout.status.id} rowIndex={rowIndex} table={table} />
        </CardStatus>
      )}
      {layout.fields.length > 0 && (
        <CardFields>
          {layout.fields.map((column) => (
            <div key={column.id} style={{ display: "contents" }}>
              <dt>{columnLabel(column)}</dt>
              <dd>
                <CellValue row={row} columnId={column.id} rowIndex={rowIndex} table={table} />
              </dd>
            </div>
          ))}
        </CardFields>
      )}
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card variant="outlined" aria-hidden="true">
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="rounded" width={96} height={24} />
      <Skeleton variant="text" width="80%" />
      <Skeleton variant="text" width="70%" />
    </Card>
  );
}

/**
 * Whether the table offers a select-all, as its row-select column's header would: rows select, more
 * than one at a time, and select-all is not turned off.
 */
function canSelectAllCards<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): boolean {
  const { enableMultiRowSelection, enableRowSelection, enableSelectAll } = table.options;
  return Boolean(enableRowSelection) && enableSelectAll !== false && enableMultiRowSelection !== false;
}

/** The header's select-all checkbox, with the words a card list has room for beside it. */
function CardsSelectAll<TData extends MRT_RowData>(props: Readonly<{ table: MRT_TableInstance<TData> }>) {
  // The table is the same object every render, so a memoized select-all never redrew (#7171).
  "use no memo";

  return (
    <SelectAllLabel>
      <TableSelectCheckbox table={props.table} />
      Select all
    </SelectAllLabel>
  );
}

/** Whether the table has anything for the Sort sheet to offer. */
function canSortCards<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): boolean {
  // The Sort sheet also holds the columns' groupings, so a table that only groups still draws it.
  return (
    sortableColumns(table).length > 0 || columnGroupings(table.getAllLeafColumns() as MRT_Column<TData>[]).length > 0
  );
}

/**
 * The phone's Sort: a button naming the current order, and the sheet it opens. The card list draws it
 * above the cards. A table with a toolbar of its own can draw it there instead, with `sortInToolbar`,
 * and then the button takes the toolbar's look through `variant` (#7163). It draws nothing for a table
 * with nothing to sort by.
 */
export function TableSortButton<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData>; variant?: ButtonProps["variant"]; sx?: SystemStyleObject<Theme> }>,
) {
  "use no memo";

  const { table } = props;
  const [isOpen, setIsOpen] = useState(false);
  const sortLabel = currentSortLabel(table);

  if (!canSortCards(table)) return null;

  return (
    <>
      <Button
        variant={props.variant ?? "outlined"}
        size="small"
        startIcon={<SortIcon />}
        aria-label={sortLabel ? `Sort, by ${sortLabel}` : "Sort"}
        onClick={() => setIsOpen(true)}
        sx={{ minHeight: TOUCH_TARGET, minWidth: 0, ...props.sx }}
      >
        <SortButtonLabel>{sortLabel ?? "Sort"}</SortButtonLabel>
      </Button>
      <TableSortSheet table={table} open={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}

/** Whether the table is sorted other than in its default order, which the Sort icon marks with a dot. */
function isSortChanged<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  defaultSorting: readonly ColumnSort[],
): boolean {
  const current = table.getState().sorting;
  return (
    current.length !== defaultSorting.length ||
    current.some((sort, index) => sort.id !== defaultSorting[index].id || sort.desc !== defaultSorting[index].desc)
  );
}

/**
 * Sort and Filter, and the sheets they open. Drawn either as icons in the toolbar's search row, before
 * the ⋯ tools menu (`icons`), or as a row of labeled buttons above the cards, for a table whose toolbar
 * draws no tools menu (`row`). The icon has no room for the order, so its accessible name carries it,
 * a dot says it differs from the list's default order, and the Sort sheet states it at its top.
 */
function SortAndFilter<TData extends MRT_RowData>(
  props: Readonly<{
    table: MRT_TableInstance<TData>;
    sortInToolbar: boolean;
    layout: "icons" | "row";
    defaultSorting: readonly ColumnSort[];
    /** For the row: the search row draws Sort and Filter, so the row holds only Select all. */
    toolbarHasSortAndFilter?: boolean;
  }>,
) {
  "use no memo";

  const { table, layout, defaultSorting } = props;
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const leftToToolbar = layout === "row" && Boolean(props.toolbarHasSortAndFilter);
  const canSort = canSortCards(table) && !props.sortInToolbar && !leftToToolbar;
  const canFilter = filterableColumns(table).length > 0 && !leftToToolbar;
  // Select all stays above the cards: the search row has no room for its words.
  const canSelectAll = layout === "row" && canSelectAllCards(table);
  const setFilters = countSetFilters(table);
  const filterName = setFilters > 0 ? `Filter, ${setFilters} set` : "Filter";
  // The tools menu's Column filters shows the filter row on a table. A card list has no filter row, so
  // it opens the sheet instead.
  const isFilterSheetOpen = isFilterOpen || (canFilter && table.getState().showColumnFilters);

  if (!canSort && !canFilter && !canSelectAll) return null;

  const filterSheet = (
    <TableFilterSheet
      table={table}
      open={isFilterSheetOpen}
      onClose={() => {
        setIsFilterOpen(false);
        table.setShowColumnFilters(false);
      }}
    />
  );

  if (layout === "icons") {
    const sortLabel = currentSortLabel(table);
    const sortName = sortLabel ? `Sort, by ${sortLabel}` : "Sort";
    return (
      <>
        {/* The words move to the tooltips, which repeat each icon's name (#6716). */}
        {canSort && (
          <>
            <Tooltip title={sortName}>
              <IconButton aria-label={sortName} onClick={() => setIsSortOpen(true)} sx={toolbarIconSx}>
                <Badge
                  color="primary"
                  invisible={!isSortChanged(table, defaultSorting)}
                  overlap="circular"
                  variant="dot"
                >
                  <SortIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <TableSortSheet table={table} open={isSortOpen} onClose={() => setIsSortOpen(false)} />
          </>
        )}
        {canFilter && (
          <Tooltip title={filterName}>
            <IconButton aria-label={filterName} onClick={() => setIsFilterOpen(true)} sx={toolbarIconSx}>
              <Badge badgeContent={setFilters} color="primary">
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        {filterSheet}
      </>
    );
  }

  return (
    <SortAndFilterRow>
      {canSelectAll && <CardsSelectAll table={table} />}
      {canSort && <TableSortButton table={table} sx={{ flex: "1 1 auto" }} />}
      {canFilter && (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            <Badge badgeContent={setFilters} color="primary">
              <FilterListIcon />
            </Badge>
          }
          aria-label={filterName}
          onClick={() => setIsFilterOpen(true)}
          sx={{ flex: "0 0 auto" }}
        >
          Filter
        </Button>
      )}
      {/* While the search row draws Filter, its sheet is the one that opens. */}
      {canFilter && filterSheet}
    </SortAndFilterRow>
  );
}

/**
 * How a card list hears that its toolbar draws Sort and Filter. `TableCards` provides it; outside a card
 * list there is none, and the toolbar draws nothing.
 */
const CardsToolbarContext = createContext<
  | {
      countInToolbar: (change: 1 | -1) => void;
      sortInToolbar: boolean;
      defaultSorting: readonly ColumnSort[];
    }
  | undefined
>(undefined);

/**
 * Sort and Filter as icons, for the toolbar's search row. `TableToolsMenu` draws it before its ⋯ button,
 * so every card list with a tools menu has one row, `[search] [sort] [filter] [⋯]`, and no Sort row
 * above its cards (#7224). Outside a card list it draws nothing.
 */
export function TableCardSortAndFilter<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData> }>,
) {
  "use no memo";

  const cards = useContext(CardsToolbarContext);
  const countInToolbar = cards?.countInToolbar;
  // A layout effect, so the row above the cards is gone before the first paint rather than a frame later.
  useLayoutEffect(() => {
    if (!countInToolbar) return undefined;
    countInToolbar(1);
    return () => countInToolbar(-1);
  }, [countInToolbar]);

  if (!cards) return null;
  return (
    <SortAndFilter
      table={props.table}
      sortInToolbar={cards.sortInToolbar}
      layout="icons"
      defaultSorting={cards.defaultSorting}
    />
  );
}

/**
 * Hands MRT the paper the cards are drawn in, as its own paper does: full screen and the tools menu
 * read it. Out of the component, since a render may not write to the table it was handed.
 */
function paperRefOf<TData extends MRT_RowData>(table: MRT_TableInstance<TData>) {
  return (element: HTMLDivElement | null) => {
    table.refs.tablePaperRef.current = element;
  };
}

/**
 * Hands the card list to MRT as its table container, and to the table's own container ref, as MRT's
 * container hands itself to both. The list is what scrolls where the container would.
 */
function listRefOf<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  containerRef: Ref<HTMLDivElement> | undefined,
) {
  return (element: HTMLUListElement | null) => {
    const container = element as unknown as HTMLDivElement | null;
    table.refs.tableContainerRef.current = container;
    if (typeof containerRef === "function") containerRef(container);
    else if (containerRef) (containerRef as MutableRefObject<HTMLDivElement | null>).current = container;
  };
}

/**
 * The table, its rows drawn as cards. Takes the place of `MaterialReactTable` in `MRTNoMemo`. See
 * `TableCardsOptions` for `ticks` and `sortInToolbar`.
 */
export function TableCards<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData> } & TableCardsOptions>,
) {
  "use no memo";

  const { table } = props;
  const ticks = Boolean(props.ticks);
  const theme = useTheme();
  const {
    enableBottomToolbar,
    enableTopToolbar,
    localization,
    mrtTheme: { baseBackgroundColor },
    muiTableContainerProps,
    muiTablePaperProps,
    renderBottomToolbar,
    renderEmptyRowsFallback,
    renderTopToolbar,
  } = table.options;
  const { isFullScreen, isLoading, showSkeletons } = table.getState();
  // MRT's own rule for a body cell: a skeleton while the table loads, unless the page turns skeletons
  // off. While `isLoading` holds and there is no data, MRT fills the rows with placeholders whose every
  // value is null, and a card drawn from them hands the page's cells nothing to read (#6722).
  const drawsSkeletons = showSkeletons !== false && Boolean(isLoading || showSkeletons);
  const paperProps = parseFromValuesOrFunc(muiTablePaperProps, { table }) ?? {};
  const containerProps = parseFromValuesOrFunc(muiTableContainerProps, { table }) ?? {};
  // Expanded sub-rows are in the row model, each after its parent.
  const rows = table.getRowModel().rows;
  const sortInToolbar = Boolean(props.sortInToolbar);
  // How many toolbar controls draw Sort and Filter. While one does, the row above the cards draws only
  // Select all, where the table offers it.
  const [inToolbar, setInToolbar] = useState(0);
  const countInToolbar = useCallback((change: 1 | -1) => setInToolbar((count) => count + change), []);
  const defaultSorting = props.defaultSorting ?? table.options.initialState?.sorting ?? noSorting;
  const toolbarContext = useMemo(
    () => ({ countInToolbar, sortInToolbar, defaultSorting }),
    [countInToolbar, sortInToolbar, defaultSorting],
  );

  let body: ReactNode;
  if (drawsSkeletons) {
    // Keyed by place, not by row id: a placeholder row has no id of its own, and a page's getRowId
    // answers "" for every one. Shared keys left React unable to drop the skeletons once the rows came,
    // so they stayed above the real cards (#6722).
    body = rows.map((_row, index) => (
      <CardItem key={`skeleton-${index}`}>
        <SkeletonCard />
      </CardItem>
    ));
  } else if (rows.length < 1) {
    body = (
      <CardItem>
        {renderEmptyRowsFallback?.({ table }) ?? (
          <Typography color="text.secondary" sx={{ padding: 2, textAlign: "center" }}>
            {localization.noRecordsToDisplay}
          </Typography>
        )}
      </CardItem>
    );
  } else {
    body = rows.map((row, rowIndex) => (
      <CardItem key={row.id} sx={{ marginLeft: row.depth * SUB_ROW_INDENT }}>
        <TableCard row={row} rowIndex={rowIndex} table={table} ticks={ticks} />
      </CardItem>
    ));
  }

  return (
    <Paper
      elevation={2}
      {...paperProps}
      ref={paperRefOf(table)}
      style={{
        ...(isFullScreen
          ? {
              bottom: 0,
              height: "100dvh",
              left: 0,
              margin: 0,
              maxHeight: "100dvh",
              maxWidth: "100dvw",
              padding: 0,
              position: "fixed",
              right: 0,
              top: 0,
              width: "100dvw",
              zIndex: theme.zIndex.modal,
            }
          : {}),
        ...paperProps.style,
      }}
      sx={(paperTheme: Theme) => ({
        backgroundColor: baseBackgroundColor,
        backgroundImage: "unset",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        // The toolbars keep their height, and the cards take what is left and scroll in it. Left to
        // flex, a long page of cards shrank the top toolbar and cut its search box in half.
        "& > *": { flexShrink: 0 },
        "& > ul": { flex: "1 1 0", minHeight: 0 },
        ...(parseFromValuesOrFunc(paperProps.sx, paperTheme) as object),
      })}
      data-testid="table-cards"
    >
      <CardsToolbarContext.Provider value={toolbarContext}>
        {enableTopToolbar !== false &&
          (parseFromValuesOrFunc(renderTopToolbar, { table }) ?? <MRT_TopToolbar table={table} />)}
      </CardsToolbarContext.Provider>
      <SortAndFilter
        table={table}
        sortInToolbar={sortInToolbar}
        layout="row"
        defaultSorting={defaultSorting}
        toolbarHasSortAndFilter={inToolbar > 0}
      />
      <CardList
        ref={listRefOf(table, containerProps.ref)}
        aria-label="Rows"
        aria-busy={drawsSkeletons}
        onScroll={containerProps.onScroll as UIEventHandler<HTMLUListElement> | undefined}
      >
        {body}
      </CardList>
      {enableBottomToolbar !== false &&
        (parseFromValuesOrFunc(renderBottomToolbar, { table }) ?? <MRT_BottomToolbar table={table} />)}
    </Paper>
  );
}
